/**
 * HomeTile — navigation tile (R-14 anchoring + R-16 anatomy)
 *
 * Top-to-bottom order (load-bearing per R-16):
 *   icon    → recognition
 *   title   → the destination name
 *   subtext → one line describing the destination
 *   metadata → count / status / relative-time text at the foot
 *
 * The tile is a single anchor (R-14): the whole card is clickable, middle-
 * clickable, and announces its destination to screen readers.
 *
 * Props:
 *   icon:    ReactNode — the icon element to render at top (required)
 *   title:   string    — destination name (required)
 *   to:      string    — destination path (required); routes via react-router
 *   subtext: string    — one-line description below the title
 *   meta:    string | string[] -- ASS-83: up to two labelled lines at the
 *            foot, plain text in the existing meta style. A single string
 *            still renders as one line (backward compatible with existing
 *            callers that pass one).
 *   loading: boolean
 *   error:   string | null
 *   onRetry: () => void
 *
 * R-16 guards respected:
 *   🛑 metric slot is typed for text, not numerals — plain body2, no heading.
 *   🛑 the count moves to metadata; no dominant number.
 *   🛑 no in-card "View all…" footer control (kept from R-14).
 *   🛑 icons come from the app's icon set (MUI icons-material, consistent with
 *      NavDrawer's use).
 */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Skeleton,
  Box,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';

export default function HomeTile({
  icon,
  title,
  to,
  subtext,
  meta,
  loading = false,
  error = null,
  onRetry,
  sx = {},
}) {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        ...sx,
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={to}
        sx={{
          height: '100%',
          // R-17: tighter padding at phone widths so metadata doesn't clip at
          // narrow viewports; standard 24px at sm+.
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          // R-17: minHeight relaxes on phone so a stacked pair fits above the
          // fold on typical device heights (568-812px).
          minHeight: { xs: 140, sm: 180 },
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <CardContent sx={{ p: 0, width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* Icon — recognition at the top */}
          {icon && (
            <Box
              sx={{
                color: theme.palette.text.primary,
                mb: { xs: 1.5, sm: 2 },
                display: 'flex',
                // R-17: icon scales slightly at phone widths so the visual
                // hierarchy stays right when the card is shorter.
                fontSize: { xs: 28, sm: 32 },
                '& svg': { fontSize: 'inherit' },
              }}
            >
              {icon}
            </Box>
          )}

          {/* Title — destination name */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: subtext ? 0.5 : 2,
            }}
          >
            {title}
          </Typography>

          {/* Subtext — one line describing the destination */}
          {subtext && (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              {subtext}
            </Typography>
          )}

          {/* Metadata — count / status text at the foot. Body2, not a heading
              (R-16 L-3: slot is typed for text, not numerals). Fills remaining
              space via mt: 'auto' so the tiles line up regardless of subtext
              length differences. */}
          <Box sx={{ mt: 'auto' }}>
            {loading ? (
              <Skeleton variant="text" width="40%" height={20} />
            ) : error ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
                {onRetry && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRetry(); }}
                    color="error"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ) : meta ? (
              Array.isArray(meta) ? (
                meta.map((line, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {line}
                  </Typography>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {meta}
                </Typography>
              )
            ) : null}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
